import { createUseStyles, withTheme } from '@glu/theming';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import Actions from './Actions';
import Calendar from './Calendar';
import TimePicker from './TimePicker/TimePicker';
import styles from './DateSelect.styles';
import { DropdownProvider } from './DropdownContext';

const useStyles = createUseStyles(styles);

const getTime = (date) => ({
  hour: date?.hour(),
  minute: date?.minute(),
  second: date?.second(),
});

const DateSelect = (props) => {
  const {
    alwaysStackCalendars,
    autoApply,
    dateMax,
    dynamicMaxRange,
    head,
    numberOfMonths,
    onApply,
    onClose,
    selectedEndDate,
    selectedStartDate,
    setHead,
    setSelectedEndDate,
    setSelectedStartDate,
    singleDatePicker,
    timePicker,
    timePickerDefaultEndTime,
    timePickerDefaultStartTime,
    timePickerRange,
    weekNumbers,
  } = props;

  const classes = useStyles();

  const [workflow, setWorkflow] = useState();
  const [dyanmicMaxRangeDate, setDynamicMaxRangeDate] = useState(undefined);

  const prevSelectedStartDate = useRef(
    selectedStartDate || moment().set({ ...timePickerDefaultStartTime })
  );
  const prevSelectedEndDate = useRef(
    selectedEndDate || moment().set({ ...timePickerDefaultEndTime })
  );

  // useEffect keeps the previousValue, to prevent resetting timePicker
  // And set default Time
  useEffect(() => {
    if (selectedStartDate) {
      prevSelectedStartDate.current = selectedStartDate;
    }
    if (selectedEndDate) {
      prevSelectedEndDate.current = selectedEndDate;
    }
  }, [selectedStartDate, selectedEndDate]);

  const handleDayClick = (date) => {
    const accurateDate = moment(date).set({ ...getTime(prevSelectedEndDate.current) });
    if (singleDatePicker) {
      setWorkflow('completed');
      date.set({ ...getTime(prevSelectedStartDate.current) });
      setSelectedStartDate(date);
      setSelectedEndDate(date);
    } else if (
      workflow === 'started' &&
      selectedStartDate &&
      accurateDate.valueOf() >= selectedStartDate.valueOf()
    ) {
      setWorkflow('completed');
      setSelectedStartDate(selectedStartDate);
      date.set({ ...getTime(prevSelectedEndDate.current) });
      setSelectedEndDate(date);
    } else {
      setWorkflow('started');
      if (dynamicMaxRange) {
        setDynamicMaxRangeDate(moment(date).add(dynamicMaxRange, 'days').format('YYYY-MM-DD'));
      }
      date.set({ ...getTime(prevSelectedStartDate.current) });
      setSelectedStartDate(date);
      setSelectedEndDate(undefined);
    }
  };

  const handleDayHover = (date) => {
    const accurateDate = moment(date).set({ ...getTime(prevSelectedEndDate.current) });

    if (workflow === 'started' && accurateDate.valueOf() >= selectedStartDate.valueOf()) {
      date.set({ ...getTime(prevSelectedEndDate.current) });
      setSelectedStartDate(selectedStartDate);
      setSelectedEndDate(date);
    }
  };

  const handleClickApply = () => {
    onApply(selectedStartDate, selectedEndDate);
  };

  if (autoApply && workflow === 'completed') {
    setWorkflow(undefined);
    onApply(selectedStartDate, selectedEndDate);
  }

  const months = Array(numberOfMonths).fill(0);

  return (
    <DropdownProvider>
      <div
        className={`${classes.dateSelect} ${weekNumbers ? classes.weekNumbers : ''} ${
          timePickerRange ? classes.dateSelectRange : ''
        }`}
        data-qa="date-select"
      >
        <div
          className={classnames(classes.calendars, {
            [classes.alwaysStackCalendars]: alwaysStackCalendars,
          })}
        >
          {months.map((month, index) => {
            const first = index === 0;
            const last = index === months.length - 1;
            return (
              <div
                key={moment(head).add(index, 'month').format('MM')}
                className={classes.calendar}
                data-qa={`${first ? 'first' : last ? 'last' : 'middle'}-calendar`}
              >
                <Calendar
                  {...props}
                  dateMax={dyanmicMaxRangeDate || dateMax}
                  first={first}
                  handleDayClick={handleDayClick}
                  handleDayHover={handleDayHover}
                  head={moment(head).add(index, 'month')}
                  index={index + 1}
                  last={last}
                  selectedEndDate={selectedEndDate}
                  selectedStartDate={selectedStartDate}
                  setHead={(date) => {
                    if (last) {
                      setHead(moment(date).add(-numberOfMonths + 1, 'month'));
                    } else {
                      setHead(date);
                    }
                  }}
                  weekNumbers={weekNumbers}
                />
              </div>
            );
          })}
        </div>
        {timePickerRange && (
          <div className={classes.row}>
            <TimePicker
              {...props}
              className={classes.startTime}
              dataQa="timepicker-start"
              dateTime={selectedStartDate}
              disabled={!selectedStartDate}
              hour={selectedStartDate?.hour() || prevSelectedStartDate.current.hour()}
              label="Start Time"
              minute={selectedStartDate?.minute() || prevSelectedStartDate.current.minute()}
              second={selectedStartDate?.second() || prevSelectedStartDate.current.second()}
              setDateTime={setSelectedStartDate}
            />
            <TimePicker
              {...props}
              className={classes.endTime}
              dataQa="timepicker-end"
              dateTime={selectedEndDate}
              disabled={!selectedEndDate}
              hour={selectedEndDate ? selectedEndDate.hour() : prevSelectedEndDate.current.hour()}
              label="End Time"
              minute={
                selectedEndDate ? selectedEndDate.minute() : prevSelectedEndDate.current.minute()
              }
              second={
                selectedEndDate ? selectedEndDate.second() : prevSelectedEndDate.current.second()
              }
              setDateTime={setSelectedEndDate}
            />
          </div>
        )}
        <div className={!autoApply || timePicker ? classes.row : ''}>
          {!autoApply && (
            <Actions
              {...props}
              disabled={!selectedEndDate}
              onClickApply={handleClickApply}
              onClickCancel={onClose}
            />
          )}
          {timePicker && (
            <TimePicker
              {...props}
              dateTime={selectedStartDate}
              disabled={!selectedStartDate}
              hour={
                selectedStartDate ? selectedStartDate.hour() : prevSelectedStartDate.current.hour()
              }
              minute={
                selectedStartDate
                  ? selectedStartDate.minute()
                  : prevSelectedStartDate.current.minute()
              }
              second={
                selectedStartDate
                  ? selectedStartDate.second()
                  : prevSelectedStartDate.current.second()
              }
              setDateTime={setSelectedStartDate}
            />
          )}
        </div>
      </div>
    </DropdownProvider>
  );
};

DateSelect.propTypes = {
  alwaysStackCalendars: PropTypes.bool.isRequired,
  autoApply: PropTypes.bool.isRequired,
  dateMax: PropTypes.string.isRequired,
  dynamicMaxRange: PropTypes.number,
  head: PropTypes.shape({}).isRequired,
  numberOfMonths: PropTypes.number.isRequired,
  onApply: PropTypes.func,
  onClose: PropTypes.func,
  selectedEndDate: PropTypes.shape({
    hour: PropTypes.func,
    minute: PropTypes.func,
    second: PropTypes.func,
  }),
  selectedStartDate: PropTypes.shape({
    hour: PropTypes.func,
    minute: PropTypes.func,
    second: PropTypes.func,
  }),
  setHead: PropTypes.func.isRequired,
  setSelectedEndDate: PropTypes.func.isRequired,
  setSelectedStartDate: PropTypes.func.isRequired,
  singleDatePicker: PropTypes.bool.isRequired,
  timePicker: PropTypes.bool.isRequired,
  timePickerDefaultEndTime: PropTypes.shape({
    hour: PropTypes.number,
    minute: PropTypes.number,
    second: PropTypes.number,
  }).isRequired,
  timePickerDefaultStartTime: PropTypes.shape({
    hour: PropTypes.number,
    minute: PropTypes.number,
    second: PropTypes.number,
  }).isRequired,
  timePickerRange: PropTypes.bool.isRequired,
  weekNumbers: PropTypes.bool.isRequired,
};

DateSelect.defaultProps = {
  dynamicMaxRange: undefined,
  onApply: undefined,
  onClose: undefined,
  selectedEndDate: undefined,
  selectedStartDate: undefined,
};

export default withTheme(DateSelect);
